// src/components/EmployeeForm.js
import { useState, useRef } from "react";
import { Input, Select, Textarea, Btn, Spinner, Alert, Avatar } from "./UI";
import { uploadEmployeePhoto, uploadAttachment, deleteFile } from "../firebase/services";

const uid = () => Math.random().toString(36).slice(2, 9);

const EMPTY = {
  name: "", role: "", dept: "", joined: "", dob: "",
  phone: "", email: "", status: "current", address: "",
  photoURL: null, photoPath: null,
  attachments: [], salary: "", notes: ""
};

export default function EmployeeForm({ emp, onSave, onClose }) {
  const [f, setF]           = useState(emp ? { ...EMPTY, ...emp } : { ...EMPTY, id: uid() });
  const [photoProgress, setPhotoProgress] = useState(null);
  const [attachProgress, setAttachProgress] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const photoRef = useRef();
  const fileRef  = useRef();

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // ── Photo upload ──────────────────────────────────────────────
  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoProgress(0);
    try {
      const url = await uploadEmployeePhoto(f.id, file, setPhotoProgress);
      set("photoURL", url);
      set("photoPath", `employees/${f.id}/photo/photo.${file.name.split(".").pop()}`);
    } catch (err) {
      setError("Photo upload failed: " + err.message);
    } finally {
      setPhotoProgress(null);
    }
  };

  // ── Attachment upload ─────────────────────────────────────────
  const handleAttach = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const tmpId = uid();
      setAttachProgress(p => ({ ...p, [tmpId]: 0 }));
      try {
        const att = await uploadAttachment(f.id, file, (pct) =>
          setAttachProgress(p => ({ ...p, [tmpId]: pct }))
        );
        att.id = tmpId;
        setF(prev => ({ ...prev, attachments: [...(prev.attachments || []), att] }));
      } catch (err) {
        setError("Attachment upload failed: " + err.message);
      } finally {
        setAttachProgress(p => { const n = { ...p }; delete n[tmpId]; return n; });
      }
    }
    e.target.value = "";
  };

  const removeAttachment = async (att) => {
    if (att.path) await deleteFile(att.path).catch(() => {});
    setF(prev => ({ ...prev, attachments: prev.attachments.filter(a => a.id !== att.id) }));
  };

  // ── Save ──────────────────────────────────────────────────────
  const save = async () => {
    if (!f.name.trim()) { setError("Employee name is required."); return; }
    setSaving(true);
    setError("");
    try {
      await onSave(f);
    } catch (err) {
      setError("Save failed: " + err.message);
      setSaving(false);
    }
  };

  const uploadingAny = photoProgress !== null || Object.keys(attachProgress).length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {error && <Alert type="error">{error}</Alert>}

      {/* Photo */}
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <Avatar name={f.name || "?"} size={68} photo={f.photoURL} />
        <div>
          <Btn variant="ghost" size="sm" onClick={() => photoRef.current.click()} disabled={photoProgress !== null}>
            {photoProgress !== null ? <><Spinner size={13} /> Uploading {photoProgress}%</> : "📷 Upload Photo"}
          </Btn>
          <input ref={photoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} />
          <div style={{ fontSize: 11, color: "var(--slate-light)", marginTop: 4 }}>JPG / PNG — stored in Firebase Storage</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input label="Full Name *" value={f.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Ananya Sharma" />
        <Input label="Role / Designation" value={f.role} onChange={e => set("role", e.target.value)} placeholder="e.g. Senior Teacher" />
        <Input label="Department" value={f.dept} onChange={e => set("dept", e.target.value)} placeholder="e.g. Science" />
        <Input label="Salary (₹)" type="number" value={f.salary} onChange={e => set("salary", e.target.value)} placeholder="e.g. 45000" />
        <Input label="Date Joined" type="date" value={f.joined} onChange={e => set("joined", e.target.value)} />
        <Input label="Date of Birth" type="date" value={f.dob} onChange={e => set("dob", e.target.value)} />
        <Input label="Phone" value={f.phone} onChange={e => set("phone", e.target.value)} placeholder="10-digit number" />
        <Input label="Email" type="email" value={f.email} onChange={e => set("email", e.target.value)} placeholder="name@rka.edu" />
        <Select label="Status" value={f.status} onChange={e => set("status", e.target.value)}>
          <option value="current">Current Employee</option>
          <option value="past">Past Employee</option>
        </Select>
      </div>

      <Textarea label="Address" value={f.address} onChange={e => set("address", e.target.value)} rows={2} placeholder="Full residential address" />
      <Textarea label="Notes / Remarks" value={f.notes} onChange={e => set("notes", e.target.value)} rows={2} />

      {/* Attachments */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6, display: "block", marginBottom: 8 }}>
          Documents & Attachments
        </label>
        <div onClick={() => fileRef.current.click()}
          style={{ border: "2px dashed #d4d0c8", borderRadius: 10, padding: "18px 20px", cursor: "pointer", textAlign: "center", color: "var(--slate-light)", fontSize: 13, transition: "border .2s" }}
          onMouseEnter={e => e.currentTarget.style.borderColor = "var(--rust)"}
          onMouseLeave={e => e.currentTarget.style.borderColor = "#d4d0c8"}>
          📎 Click to attach files — PDFs, images, documents<br />
          <span style={{ fontSize: 11 }}>Files are uploaded directly to Firebase Storage</span>
          <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={handleAttach} />
        </div>

        {/* Upload progress indicators */}
        {Object.entries(attachProgress).map(([id, pct]) => (
          <div key={id} style={{ marginTop: 8, padding: "8px 12px", background: "#f0f4ff", borderRadius: 8, fontSize: 12, color: "var(--slate)" }}>
            <Spinner size={12} color="var(--slate)" /> &nbsp;Uploading… {pct}%
          </div>
        ))}

        {/* Existing attachments */}
        {(f.attachments || []).length > 0 && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            {f.attachments.map(a => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--cream)", borderRadius: 8, fontSize: 13 }}>
                <span>📄</span>
                <a href={a.url} target="_blank" rel="noreferrer" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--slate)", textDecoration: "none" }}>{a.name}</a>
                <span style={{ color: "var(--slate-light)", fontSize: 11, flexShrink: 0 }}>{(a.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => removeAttachment(a)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--danger)", fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
        <Btn variant="ghost" onClick={onClose} disabled={saving || uploadingAny}>Cancel</Btn>
        <Btn onClick={save} disabled={saving || uploadingAny}>
          {saving ? <><Spinner size={14} color="#fff" /> Saving…</> : "Save Employee"}
        </Btn>
      </div>
    </div>
  );
}
